import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import {
  Phone, Menu, User, LogIn, LogOut, Plane, FileText, Settings, LayoutDashboard, Home, MapPinned, MoonStar, Radar,
  BadgePercent, Ticket, Info, Briefcase, Headset, CircleUserRound, UserRoundPlus, ChevronDown,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import CurrencySelector from './CurrencySelector';

const NAV_LINKS = [
  { name: 'Home', href: '#home', isHome: true, icon: Home },
  { name: 'Destinations', href: '#destinations', icon: MapPinned },
  { name: 'Umrah Quote', href: '/umrah/quote', isExternal: true, highlight: true, icon: MoonStar },
  { name: 'Track Flight', href: '/track', isExternal: true, icon: Radar },
  { name: 'Deals', href: '#deals', icon: BadgePercent },
  { name: 'Manage Booking', href: '#manage', isManageBooking: true, icon: Ticket },
  { name: 'About', href: '#about', icon: Info },
  { name: 'Careers', href: '/careers', isExternal: true, icon: Briefcase },
  { name: 'Contact', href: '#contact', icon: Headset },
];

const slug = (name) => name.toLowerCase().replace(/\s+/g, '-');

const Avatar = ({ user, className = '' }) => {
  if (user?.picture) return <img src={user.picture} alt={user.name} className={`rounded-full object-cover ring-2 ring-[#FF6600]/70 ${className}`} />;
  if (user) {
    return (
      <span className={`rounded-full bg-[#FF6600] text-white font-bold flex items-center justify-center ${className}`}>
        {(user.name?.charAt(0) || user.email?.charAt(0) || 'U').toUpperCase()}
      </span>
    );
  }
  return <CircleUserRound className={`text-slate-600 ${className}`} strokeWidth={1.75} />;
};

const AccountMenu = ({ user, onSignIn, onSignOut, onManageBooking, onMyAccount, onAdminDashboard }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className={`group flex items-center gap-2 rounded-full border transition-colors h-9 pl-0.5 pr-0.5 xl:pr-3 ${user ? 'border-[#FF6600]/30 bg-orange-50/60 hover:bg-orange-50' : 'border-slate-200 bg-white hover:border-[#FF6600] hover:bg-orange-50/60'}`}
        aria-label={user ? 'Account menu' : 'Sign in or create an account'}
        data-testid="header-account-btn"
      >
        <Avatar user={user} className="h-8 w-8 text-sm" />
        {user ? (
          <span className="hidden xl:block text-sm font-semibold text-slate-800 max-w-[110px] truncate">{user.name?.split(' ')[0] || 'Account'}</span>
        ) : (
          <span className="hidden xl:block text-sm font-semibold text-slate-700 group-hover:text-[#FF6600]">Sign in</span>
        )}
        <ChevronDown className="hidden xl:block h-3.5 w-3.5 text-slate-400" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-64 p-1.5 rounded-xl shadow-xl border-slate-200" data-testid="header-account-menu">
      {user ? (
        <>
          <DropdownMenuLabel className="flex items-center gap-3 py-3">
            <Avatar user={user} className="h-10 w-10 text-base" />
            <div className="flex flex-col min-w-0">
              <span className="font-semibold truncate">{user.name}</span>
              <span className="text-xs text-slate-500 font-normal truncate">{user.email}</span>
              {user.role && user.role !== 'customer' && (
                <span className={`text-[11px] mt-1 px-1.5 py-0.5 rounded w-fit font-semibold ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {user.role === 'admin' ? 'Admin' : 'Staff'}
                </span>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onMyAccount} className="cursor-pointer rounded-lg" data-testid="account-menu-my-account"><User className="h-4 w-4 mr-2" />My Account</DropdownMenuItem>
          <DropdownMenuItem onClick={onMyAccount} className="cursor-pointer rounded-lg"><Plane className="h-4 w-4 mr-2" />My Bookings</DropdownMenuItem>
          <DropdownMenuItem onClick={onMyAccount} className="cursor-pointer rounded-lg"><FileText className="h-4 w-4 mr-2" />Invoices</DropdownMenuItem>
          <DropdownMenuItem onClick={onManageBooking} className="cursor-pointer rounded-lg"><Settings className="h-4 w-4 mr-2" />Manage Booking</DropdownMenuItem>
          {(user.role === 'staff' || user.role === 'admin') && (
            <DropdownMenuItem onClick={onAdminDashboard} className="cursor-pointer rounded-lg text-blue-600"><Plane className="h-4 w-4 mr-2" />All Bookings</DropdownMenuItem>
          )}
          {user.role === 'admin' && (
            <DropdownMenuItem onClick={onAdminDashboard} className="cursor-pointer rounded-lg text-purple-600" data-testid="account-menu-admin"><LayoutDashboard className="h-4 w-4 mr-2" />Admin Panel</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSignOut} className="cursor-pointer rounded-lg text-red-600 focus:text-red-600" data-testid="account-menu-signout"><LogOut className="h-4 w-4 mr-2" />Sign Out</DropdownMenuItem>
        </>
      ) : (
        <>
          <DropdownMenuLabel className="py-2">
            <div className="font-semibold text-slate-900">Welcome to Flight380</div>
            <div className="text-xs text-slate-500 font-normal">Sign in to see your bookings, e-tickets and invoices.</div>
          </DropdownMenuLabel>
          <div className="px-1.5 pb-1.5 pt-1 flex flex-col gap-1.5">
            <Button className="w-full h-10 bg-[#FF6600] hover:bg-[#E65A00] text-white font-bold rounded-lg" onClick={() => onSignIn && onSignIn('login')} data-testid="header-signin-btn">
              <LogIn className="h-4 w-4 mr-2" />Sign In
            </Button>
            <Button variant="outline" className="w-full h-10 border-slate-200 hover:border-[#FF6600] hover:bg-orange-50 text-slate-800 font-semibold rounded-lg" onClick={() => onSignIn && onSignIn('register')} data-testid="header-signup-btn">
              <UserRoundPlus className="h-4 w-4 mr-2" />Create free account
            </Button>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onManageBooking} className="cursor-pointer rounded-lg" data-testid="account-menu-manage-booking"><Ticket className="h-4 w-4 mr-2" />Manage a booking</DropdownMenuItem>
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg"><a href="/track"><Radar className="h-4 w-4 mr-2" />Track a flight</a></DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Header = ({ onNavigateHome, user, onSignIn, onSignOut, onHeightChange, onManageBooking, onMyAccount, onAdminDashboard, selectedCurrency, onCurrencyChange, departureAirport }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const measureHeight = () => {
      if (headerRef.current && onHeightChange) onHeightChange(headerRef.current.getBoundingClientRect().height);
    };
    measureHeight();
    const initialTimer = setTimeout(measureHeight, 100);
    const secondTimer = setTimeout(measureHeight, 500);
    let resizeObserver;
    if (headerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measureHeight);
      resizeObserver.observe(headerRef.current);
    }
    window.addEventListener('resize', measureHeight);
    document.fonts?.ready?.then(measureHeight);
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(secondTimer);
      window.removeEventListener('resize', measureHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [onHeightChange]);

  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isActive = (link) => (link.isExternal ? path.startsWith(link.href) : link.isHome && path === '/' && !window.location.search);

  const handleNavClick = (e, link) => {
    if (link.isExternal) return;
    if (path !== '/' && link.href && link.href.startsWith('#')) {
      e.preventDefault();
      window.location.href = `/${link.href}`;
      return;
    }
    if (link.isHome && onNavigateHome) {
      e.preventDefault();
      onNavigateHome();
    } else if (link.isManageBooking && onManageBooking) {
      e.preventDefault();
      onManageBooking();
    }
    setMobileMenuOpen(false);
  };

  const handleLogoClick = (e) => {
    if (path !== '/') {
      e.preventDefault();
      window.location.href = '/';
      return;
    }
    if (onNavigateHome) {
      e.preventDefault();
      onNavigateHome();
    }
  };

  return (
    <header ref={headerRef} className="fixed top-0 w-full bg-white/95 supports-[backdrop-filter]:bg-white/85 backdrop-blur-md border-b border-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_24px_-16px_rgba(15,23,42,0.25)] z-50" data-testid="site-header">
      <div className="container mx-auto px-3 sm:px-4 h-16 md:h-[72px] flex items-center gap-2 md:gap-3">
        {/* Logo */}
        <a href="#home" onClick={handleLogoClick} className="flex items-center shrink-0 cursor-pointer" aria-label="Flight380 home" data-testid="header-logo">
          <img src="/logo-f380.png" alt="Flight380" className="h-9 md:h-11 w-auto" />
        </a>

        {/* Primary navigation — icons from md, labels from xl, always a single line */}
        <nav className="hidden md:flex items-center justify-center gap-0.5 2xl:gap-1 flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Primary">
          {NAV_LINKS.map((link) => {
            const Icon = link.icon;
            const active = isActive(link);
            const cls = link.highlight
              ? 'bg-[#FF6600] text-white hover:bg-[#E65A00] shadow-sm shadow-orange-500/30 px-3 2xl:px-4'
              : active
                ? 'text-[#FF6600] bg-orange-50 px-2.5 2xl:px-3'
                : 'text-slate-700 hover:text-[#FF6600] hover:bg-orange-50/80 px-2.5 2xl:px-3';
            return (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                title={link.name}
                aria-label={link.name}
                aria-current={active ? 'page' : undefined}
                data-testid={link.highlight ? 'header-umrah-quote' : `header-nav-${slug(link.name)}`}
                className={`relative inline-flex items-center gap-1.5 whitespace-nowrap h-9 shrink-0 rounded-full text-[13px] 2xl:text-sm font-semibold transition-colors duration-200 ${cls}`}
              >
                {/* md–lg: icon only · xl: label only · 2xl: icon + label (the Umrah CTA always keeps its icon) */}
                <Icon className={`h-[18px] w-[18px] shrink-0 ${link.highlight ? '' : 'xl:hidden 2xl:block'}`} strokeWidth={2} />
                <span className={link.highlight ? 'hidden lg:inline' : 'hidden xl:inline'}>{link.name}</span>
                {active && !link.highlight && <span className="hidden xl:block absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[#FF6600]" />}
              </a>
            );
          })}
        </nav>

        {/* Right cluster: compact currency · account avatar · mobile menu */}
        <div className="ml-auto md:ml-0 flex items-center gap-1.5 sm:gap-2 shrink-0">
          <div data-testid="header-currency-mobile">
            <CurrencySelector
              selectedCurrency={selectedCurrency || 'GBP'}
              onCurrencyChange={onCurrencyChange}
              departureAirport={departureAirport}
              showLabel={false}
              size="compact"
            />
          </div>

          <AccountMenu user={user} onSignIn={onSignIn} onSignOut={onSignOut} onManageBooking={onManageBooking} onMyAccount={onMyAccount} onAdminDashboard={onAdminDashboard} />

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden h-9 w-9 rounded-full border border-slate-200 bg-white hover:bg-orange-50 hover:border-[#FF6600]" aria-label="Open menu" data-testid="header-mobile-menu-btn">
                <Menu className="h-5 w-5 text-slate-800" strokeWidth={2.25} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" aria-describedby={undefined} className="w-[85vw] max-w-[320px] p-0 border-l-4 border-l-[#FF6600] z-[60]">
              <SheetTitle className="sr-only">Main menu</SheetTitle>
              <div className="flex flex-col h-full bg-white">
                <div className="p-5 bg-[#222222]">
                  <img src="/logo-f380.png" alt="Flight380" className="h-10 brightness-0 invert" />
                </div>

                {user && (
                  <div className="flex items-center gap-3 p-4 border-b bg-slate-50">
                    <Avatar user={user} className="h-12 w-12 text-lg" />
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 truncate">{user.name}</div>
                      <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                  </div>
                )}

                <nav className="flex flex-col p-3 gap-1 flex-1 overflow-y-auto" aria-label="Mobile">
                  {NAV_LINKS.map((link) => {
                    const Icon = link.icon;
                    return (
                      <SheetClose asChild key={link.name}>
                        <a
                          href={link.href}
                          onClick={(e) => handleNavClick(e, link)}
                          data-testid={`mobile-nav-${slug(link.name)}`}
                          className={`flex items-center gap-3 text-[15px] font-semibold py-3 px-3.5 rounded-xl transition-colors ${link.highlight ? 'bg-[#FF6600] text-white hover:bg-[#E65A00]' : 'text-slate-800 hover:bg-orange-50 hover:text-[#FF6600]'}`}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          {link.name}
                        </a>
                      </SheetClose>
                    );
                  })}
                </nav>

                <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-3">
                  <a href="tel:01908220000" className="flex items-center justify-center gap-3 text-white font-bold p-3.5 bg-[#222222] hover:bg-black rounded-xl shadow-sm transition-colors">
                    <Phone className="h-5 w-5 text-[#FF6600]" />
                    <div className="flex flex-col leading-tight text-sm">
                      <span>01908 220000</span>
                      <span>01582 808050</span>
                    </div>
                  </a>
                  <a
                    href="https://wa.me/4401908220000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 p-3 bg-[#25D366] text-white font-semibold rounded-xl shadow-sm hover:bg-[#1ebe5b] transition-colors"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Chat on WhatsApp
                  </a>
                  {user && (
                    <Button variant="outline" className="w-full h-11 border-slate-200 hover:border-red-300 hover:bg-red-50 text-red-600 font-semibold rounded-xl" onClick={() => { onSignOut(); setMobileMenuOpen(false); }}>
                      <LogOut className="h-4 w-4 mr-2" />Sign Out
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
